import { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import {
  AppCard,
  AppCheckbox,
  AppDatePicker,
  AppInput,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import { FieldArray } from 'formik';
import { UnmountClosed } from 'react-collapse/lib/UnmountClosed';
import Moment from 'moment';
import ReactDOMServer from 'react-dom/server';
import Translator from '../../../../components/helpers/Translator';
import _ from 'lodash';

export default function ModuleComsaEnvironmentalIndustrialPpiCard(props) {
  const {
    index,
    filterApplicable,
    responsibleOptions,
    frequencyOptions,
    inspectionDefaultValues,
    values,
    setFieldValue,
  } = props;
  const [expanded, setExpanded] = useState(false);
  const [
    translatedInspectionDefaultValues,
    setTranslatedInspectionDefaultValues,
  ] = useState(null);
  const [pointTypeOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="point.of.warning" />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="point.of.waiting" />
      ),
      value: 1,
    },
  ]);

  const hasChanged = useCallback(
    () =>
      !_.isEqual(
        values?.inspection?.[index],
        translatedInspectionDefaultValues
      ),
    [values, index, translatedInspectionDefaultValues]
  );

  const translatedIDV = useCallback(() => {
    const tempInspectionDefaultValues = { ...inspectionDefaultValues[index] };

    tempInspectionDefaultValues.description = ReactDOMServer.renderToString(
      <Translator
        file="module-comsaEnvironmental"
        name={inspectionDefaultValues[index].description}
      />
    );
    tempInspectionDefaultValues.rule = ReactDOMServer.renderToString(
      <Translator
        file="module-comsaEnvironmental"
        name={inspectionDefaultValues[index].rule}
      />
    );
    tempInspectionDefaultValues.inspectionType = ReactDOMServer.renderToString(
      <Translator
        file="module-comsaEnvironmental"
        name={inspectionDefaultValues[index].inspectionType}
      />
    );
    tempInspectionDefaultValues.acceptanceRejection = ReactDOMServer.renderToString(
      <Translator
        file="module-comsaEnvironmental"
        name={inspectionDefaultValues[index].acceptanceRejection}
      />
    );
    setTranslatedInspectionDefaultValues(tempInspectionDefaultValues);
  }, [inspectionDefaultValues, index]);

  useEffect(() => {
    translatedIDV();
  }, []);

  if (
    (filterApplicable &&
      !values?.automaticApplicable?.[index] &&
      !values?.applicable?.[index]) ||
    !translatedInspectionDefaultValues
  ) {
    return null;
  }

  return (
    <div className="mb-2">
      <Container>
        <AppCheckbox
          title={
            values?.automaticApplicable?.[index] || values?.applicable?.[index]
              ? ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="no.applicable"
                  />,
                )
              : ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="assign.as.applicable"
                  />,
                )
          }
          value={
            values?.automaticApplicable?.[index] || values?.applicable?.[index]
          }
          className="d-inline-flex"
          onChange={(v) => setFieldValue(`applicable[${index}]`, v)}
          isDisabled={values?.automaticApplicable?.[index]}
        />
        <AppCardContainer
          bodyClassName="p-1"
          $hasChanged={hasChanged()}
          $applicable={
            values?.automaticApplicable?.[index] || values?.applicable?.[index]
          }
        >
          {hasChanged() && !inspectionDefaultValues?.[index]?.applicable && (
            <ResetValues
              title={ReactDOMServer.renderToString(
                <Translator file="module-comsaEnvironmental" name="restart" />,
              )}
              onClick={() =>
                setFieldValue(
                  `inspection[${index}]`,
                  translatedInspectionDefaultValues,
                )
              }
            >
              <AppRemixIcon
                icon="arrow-go-back"
                className="pointer"
                size={16}
              />
            </ResetValues>
          )}
          <div className="">
            <div className="info-block">
              <div className="line no-center">
                <AppTextArea
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="inspection.description"
                    />,
                  )}
                  value={
                    ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name={values?.inspection?.[index]?.description}
                      />,
                    ) ??
                    ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name={inspectionDefaultValues?.[index]?.description}
                      />,
                    )
                  }
                  onChange={(v) =>
                    setFieldValue(`inspection[${index}].description`, v)
                  }
                  height="89px"
                />
              </div>
              <div className="line no-center">
                <AppSingleSelect
                  options={responsibleOptions}
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="responsible"
                    />,
                  )}
                  value={
                    values?.inspection?.[index]?.responsible ??
                    inspectionDefaultValues?.[index]?.responsible
                  }
                  onChange={(v) =>
                    setFieldValue(`inspection[${index}].responsible`, v)
                  }
                />
                <AppInput
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="standard"
                    />,
                  )}
                  value={
                    ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name={values?.inspection?.[index]?.rule}
                      />,
                    ) ?? inspectionDefaultValues?.[index]?.rule
                  }
                  onChange={(v) =>
                    setFieldValue(`inspection[${index}].rule`, v)
                  }
                />
              </div>
            </div>
            <div className="info-block">
              <div className="line">
                <AppInput
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="inspection.type"
                    />,
                  )}
                  value={
                    values?.inspection?.[index]?.inspectionType ??
                    inspectionDefaultValues?.[index]?.inspectionType
                  }
                  onChange={(v) =>
                    setFieldValue(`inspection[${index}].inspectionType`, v)
                  }
                />
              </div>
              <div className="line">
                <AppSingleSelect
                  options={frequencyOptions}
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="intensity.or.frequency"
                    />,
                  )}
                  value={
                    values?.inspection?.[index]?.intensity ??
                    inspectionDefaultValues?.[index]?.intensity
                  }
                  onChange={(v) =>
                    setFieldValue(`inspection[${index}].intensity`, v)
                  }
                />
              </div>
              <div className="line">
                <AppInput
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="a.acceptance.r.rejection"
                    />,
                  )}
                  value={
                    ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name={values?.inspection?.[index]?.acceptanceRejection}
                      />,
                    ) ?? inspectionDefaultValues?.[index]?.acceptanceRejection
                  }
                  onChange={(v) =>
                    setFieldValue(`inspection[${index}].acceptanceRejection`, v)
                  }
                />
              </div>
            </div>
            <div className="info-block">
              <div className="line">
                <AppSingleSelect
                  options={pointTypeOptions}
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="point.type"
                    />,
                  )}
                  value={values?.inspection?.[index]?.pointType}
                  onChange={(v) =>
                    setFieldValue(`inspection[${index}].pointType`, v)
                  }
                  isClearable
                />
              </div>
              {values?.inspection?.[index]?.pointType !== undefined &&
                values?.inspection?.[index]?.pointType !== null && (
                  <div className="line">
                    <AppInput
                      title={
                        values?.inspection?.[index]?.pointType === 1
                          ? ReactDOMServer.renderToString(
                              <Translator
                                file="module-comsaEnvironmental"
                                name="presence.by"
                              />,
                            )
                          : ReactDOMServer.renderToString(
                              <Translator
                                file="module-comsaEnvironmental"
                                name="notify.to"
                              />,
                            )
                      }
                      value={values?.inspection?.[index]?.pointTypeDescription}
                      onChange={(v) =>
                        setFieldValue(
                          `inspection[${index}].pointTypeDescription`,
                          v,
                        )
                      }
                    />
                  </div>
                )}
            </div>
          </div>
          <ExpandBtn
            title={ReactDOMServer.renderToString(
              <Translator file="module-comsaEnvironmental" name="extend" />,
            )}
            onClick={() => setExpanded(!expanded)}
          >
            <AppRemixIcon
              icon={expanded ? 'subtract' : 'add'}
              className="pointer"
              size={16}
            />
          </ExpandBtn>
        </AppCardContainer>
      </Container>
      <UnmountClosed isOpened={expanded}>
        <ExpandContainer>
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th className="col-sm-5">
                    <Translator file="module-comsaEnvironmental" name="date" />
                  </th>
                  <th className="col-sm-5">
                    <Translator
                      file="module-comsaEnvironmental"
                      name="comment"
                    />
                  </th>
                  <th className="col-sm-2 text-center">
                    <Translator
                      file="module-comsaEnvironmental"
                      name="options"
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                <FieldArray
                  name={`milestones[${index}]`}
                  render={(arrayHelpers) => (
                    <>
                      {values?.milestones?.[index] &&
                        values?.milestones?.[index].map((row, i) => (
                          <tr key={i}>
                            <td>
                              <AppDatePicker
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-comsaEnvironmental"
                                    name="date"
                                  />,
                                )}
                                value={
                                  row?.date || Moment().format('DD/MM/YYYY')
                                }
                                onChange={(date) =>
                                  setFieldValue(
                                    `milestones[${index}][${i}].date`,
                                    date,
                                  )
                                }
                                placeholder="dd/mm/yyyy"
                                showMonthDropdown
                                showYearDropdown
                              />
                            </td>
                            <td>
                              <AppInput
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-comsaEnvironmental"
                                    name="comment"
                                  />,
                                )}
                                onChange={(v) =>
                                  setFieldValue(
                                    `milestones[${index}][${i}].comment`,
                                    v,
                                  )
                                }
                                value={row?.comment}
                                isClearable
                              />
                            </td>
                            <td className="text-center align-middle">
                              <span
                                className="btn btn-danger btn-xs"
                                onClick={() =>
                                  setFieldValue(
                                    `milestones[${index}]`,
                                    values?.milestones.filter(
                                      (_, idx) => idx !== i,
                                    ),
                                  )
                                }
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-comsaReports"
                                    name="delete"
                                  />,
                                )}
                              >
                                <AppRemixIcon icon="delete-bin-6" />
                              </span>
                            </td>
                          </tr>
                        ))}
                      <tr>
                        <td colSpan={2} />
                        <td className="text-center align-middle">
                          <span
                            className="btn btn-success btn-xs"
                            onClick={() =>
                              arrayHelpers.push({
                                date: '',
                                comment: '',
                              })
                            }
                          >
                            <Translator
                              file="module-comsaEnvironmental"
                              name="add"
                            />
                          </span>
                        </td>
                      </tr>
                    </>
                  )}
                />
              </tbody>
            </table>
          </div>
        </ExpandContainer>
      </UnmountClosed>
    </div>
  );
}

const Container = styled.div`
  display: grid;
  grid-template-columns: 24px 1fr;
`;

const ExpandContainer = styled(AppCard)`
  margin-left: 24px;
`;

const ResetValues = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #ffb351;
  color: #fff;
  cursor: pointer;
`;

const ExpandBtn = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #003942;
  color: #fff;
  cursor: pointer;
`;

const AppCardContainer = styled(AppCard)`
  & > div {
    display: grid;
    grid-template-columns: ${(props) =>
      props.$hasChanged ? '24px 1fr 24px' : '1fr 24px'};
    grid-gap: 5px;
    border: 2px solid
      ${(props) =>
        props.$applicable ? '#28a745' : props.$hasChanged ? '#ffb351' : '#fff'};
  }
  .info-block {
    display: flex;
    flex-direction: column;
    gap: 10px;
    @media (min-width: 768px) {
      flex-direction: row;
    }
    .line {
      flex: 1 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      &.no-center {
        justify-content: unset;
      }
    }
  }
`;
